import Service, { inject as service } from "@ember/service"
import { A, pipe } from "@mobily/ts-belt"
import type { Course, CourseDateAnnotationSingle } from "@shared/models/course"
import { createCourseDateCustomAttributes } from "@shared/models/course"
import DateAnnotation from "@shared/models/date-annotation"
import type { Planbook } from "@shared/models/planbook"
import type {
  RotationCalendar,
  RotationCalendarDate,
  RotationCalendarRotation,
} from "@shared/models/rotation-calendar"
import ObjectId from "@shared/util/object-id-gen"
import { handleError } from "cc-frontend/error-handling"
import { prepareBumpAllLessonsForDay } from "cc-frontend/lib/actions/course/calendar/BUMP_ALL_LESSONS_FOR_DAY"
import { prepareBumpAllLessonsInRangeToDate } from "cc-frontend/lib/actions/course/calendar/BUMP_ALL_LESSONS_IN_RANGE_TO_DATE"
import { prepareBumpLesson } from "cc-frontend/lib/actions/course/calendar/BUMP_LESSON"
import { preparePullMulitpleLessonsBackwards } from "cc-frontend/lib/actions/course/calendar/PULL_MULTIPLE_LESSONS_BACKWARDS"
import prepareTurnDateOffAnd from "cc-frontend/lib/actions/course/calendar/TURN_DATE_OFF_AND"
import COURSE_DATE_ANNOTATION_DELETE_MULTIPLE from "cc-frontend/lib/actions/course/date/COURSE_DATE_ANNOTATION_DELETE_MULTIPLE"
import { prepareLessonErase } from "cc-frontend/lib/actions/lesson/LESSON_ERASE"
import pullLessonBackwardsAction from "cc-frontend/lib/actions/lesson/PULL_LESSON_BACKWARDS"
import { prepareEraseAllLessonsForDay } from "cc-frontend/lib/actions/planbook/lessons/PLANBOOK_ERASE_ALL_LESSONS_FOR_DAY"
import { prepareEraseAllLessonsForDays } from "cc-frontend/lib/actions/planbook/lessons/PLANBOOK_ERASE_ALL_LESSONS_FOR_DAYS"
import { findCourseDateCustom } from "cc-frontend/lib/find-course-date"
import moment from "cc-frontend/utils/extended-moment"
import {
  isCourseDateAnnotationSingle,
  isCourseDateCustom,
} from "cc-frontend/utils/filter-dates/course"
import _, { chain } from "lodash"
import { find, includes, map } from "lodash-es"
import type { PreparedAction } from "../lib/action-doc-builder"
import { COURSE_DATE_ANNOTATION_CREATE } from "../lib/actions/course/date/COURSE_DATE_ANNOTATION_CREATE"
import courseDateAnnotationDeleteAction from "../lib/actions/course/date/COURSE_DATE_ANNOTATION_DELETE"
import courseDateForceOnAction from "../lib/actions/course/date/COURSE_DATE_FORCE_ON"
import {
  COURSE_DATE_UNFORCE_ON,
  prepareUnforceOn,
} from "../lib/actions/course/date/COURSE_DATE_UNFORCE_ON"
import type Dispatcher from "../services/dispatcher"
import type Dialog from "./dialog"

export default class CourseCalendarEditor extends Service {
  @service dispatcher!: Dispatcher
  @service dialog!: Dialog

  /*-------------------------------------------------------------------------------------------------------
Toggle Course On/Off
--------------------------------------------------------------------------------------------------------*/

  toggleDefaultDate(
    course: Course,
    rotationCalendar: RotationCalendar,
    rotation: RotationCalendarRotation,
    semesterId: string,
    isOn: boolean
  ) {
    if (isOn) {
      this._turnOffDefault(course, rotation, semesterId)
    } else {
      this._turnOnDefault(course, rotationCalendar, rotation, semesterId)
    }
  }

  toggleCustomDate(
    course: Course,
    dateString: string,
    courseDateIsOff: boolean,
    courseDateIsForcedOn: boolean
  ) {
    if (courseDateIsOff) {
      this._turnCourseOn(course, dateString, courseDateIsForcedOn)
    } else {
      this._turnCourseOff(course, dateString, courseDateIsForcedOn)
    }
  }

  turnOffAndBumpDeleteOrNothing({
    dateString,
    cardStackId,
    course,
    planbook,
    rotationCalendar,
    dateAnnotation,
    noteAnnotation,
    bumpOrDelete,
  }: {
    dateString: string
    cardStackId: string | null
    course: Course
    planbook: Planbook
    rotationCalendar: RotationCalendar
    dateAnnotation: CourseDateAnnotationSingle
    /** A note to pin to the day alongside the off marker; it outlives turning the class on. */
    noteAnnotation?: CourseDateAnnotationSingle | null
    bumpOrDelete: "BUMP" | "DELETE" | "NO_LESSON"
  }) {
    let courseDate = findCourseDateCustom(course, dateString)
    let dateIsForcedOn =
      _.includes(course.attributes.calendar.datesForcedOn, dateString) ||
      (courseDate && courseDate.attributes.isForcedOn)
    const time = courseDate ? courseDate.attributes.time : null

    // check if it's forced on
    let unforceOnAction = dateIsForcedOn
      ? prepareUnforceOn({ dateString: dateString, course: course })
      : null

    // bump patches -- this needs to be renamed
    let bumpOrDeleteAction!: PreparedAction | null

    if (bumpOrDelete === "BUMP") {
      bumpOrDeleteAction = prepareBumpLesson({
        dateString: dateString,
        course: course,
        planbook: planbook,
        rotationCalendar: rotationCalendar,
      })
    } else if (bumpOrDelete === "DELETE" && cardStackId !== null) {
      bumpOrDeleteAction = prepareLessonErase({
        course: course,
        dateString: dateString,
        cardStackId: cardStackId,
        time,
      })
    }

    let unforceOnPatches = unforceOnAction && unforceOnAction.patches ? unforceOnAction.patches : []
    let unforceOnUndoPatches =
      unforceOnAction && unforceOnAction.undoPatches ? unforceOnAction.undoPatches : []

    let bumpOrDeletePatches =
      bumpOrDeleteAction && bumpOrDeleteAction.patches ? bumpOrDeleteAction.patches : []
    let bumpOrDeleteUndoPatches =
      bumpOrDeleteAction && bumpOrDeleteAction.undoPatches ? bumpOrDeleteAction.undoPatches : []

    // turn off patches
    this.dispatcher.dispatchPreparedAction(
      prepareTurnDateOffAnd({
        planbook: planbook,
        cardStackId: cardStackId,
        dateAnnotation: dateAnnotation,
        noteAnnotation: noteAnnotation ?? null,
        course: course,
        dateString: dateString,
        bumpOrDelete: bumpOrDelete,
        bumpOrDeletePatches: unforceOnPatches.concat(bumpOrDeletePatches),
        bumpOrDeleteUndoPatches: unforceOnUndoPatches.concat(bumpOrDeleteUndoPatches),
      })
    )
  }

  /**
   * Adds a note to a course date without turning the day off.
   */
  addNote({
    course,
    dateString,
    title,
    description,
  }: {
    course: Course
    dateString: string
    title: string
    description: string
  }) {
    // @ts-ignore The date Annotation model doesn't produce the right type signature
    let dateAnnotation: CourseDateAnnotationSingle = new DateAnnotation({
      id: ObjectId.create(),
      type: "date-annotation-course-custom-single",
      attributes: {
        title: title,
        description: description,
        isOff: false,
        dateString: dateString,
        showAnnotation: true,
      },
    })
    this.dispatcher.dispatchAction(COURSE_DATE_ANNOTATION_CREATE, {
      course: course,
      annotation: dateAnnotation,
      originalDatesOff: course.attributes.calendar.datesOff,
      calledFrom: "add-note-button",
    })
  }

  /**
  -------------------------------------------------------------------------------------------------------
  Bumping Lessons
  --------------------------------------------------------------------------------------------------------
  */

  /**
   * This will push a single lesson forward a specified amount of days.
   * Can be used anywhere the user desires to move a lesson forward. For example, on the lesson-menu
   * This shifts all cardStackIds in the course.attributes.calendar.dates down
   a day, skipping over days turned off.
   */
  bumpLesson(
    course: Course,
    dateString: string,
    planbook: Planbook,
    rotationCalendar: RotationCalendar
  ) {
    this.dispatcher.dispatchPreparedAction(
      prepareBumpLesson({
        course: course,
        dateString: dateString,
        planbook: planbook,
        rotationCalendar: rotationCalendar,
      })
    )
  }

  /**
   * This pushes all lessons forward by a specified amount
   * Used as a button on the day-menu, but can be used anywhere the user desires to move lessons forward
   * It iterates through each course and then uses the same action that's used for bumping a single lesson
   */
  bumpDayForward(
    dateString: string,
    courses: Array<Course>,
    planbook: Planbook,
    rotationCalendar: RotationCalendar
  ) {
    this.dispatcher.dispatchPreparedAction(
      prepareBumpAllLessonsForDay({
        dateString: dateString,
        courses: courses,
        planbook: planbook,
        rotationCalendar: rotationCalendar,
      })
    )
  }

  /**
   * Pushes all lessons *and unit starts/ends* out of the given range.
   */
  bumpRangeForward(
    startDate: string,
    endDate: string,
    courses: Array<Course>,
    planbook: Planbook,
    rotationCalendar: RotationCalendar
  ) {
    const validSchoolDays = rotationCalendar.attributes.schoolDays
    const range = moment.range(moment(startDate), moment(endDate))
    const dateStrings = Array.from(range.by("days"))
      .filter((moment) => validSchoolDays.includes(moment.weekday()))
      .map((moment) => moment.format("YYYY-MM-DD"))
    if (dateStrings.length === 0) return
    this.dispatcher.dispatchPreparedAction(
      prepareBumpAllLessonsInRangeToDate({
        courses,
        startDate,
        endDate,
        dateStrings,
        planbook,
        rotationCalendar,
      })
    )
  }

  /*-------------------------------------------------------------------------------------------------------
  Pull Lesson Back
  --------------------------------------------------------------------------------------------------------*/

  /**
   * Check if the coursedate has a lesson.
   */
  doesCourseDateHaveLesson(course: Course, dateString: string): boolean {
    let courseDate = findCourseDateCustom(course, dateString)
    // IF there's not course Date, there's definitely no lessons
    if (courseDate === undefined) return false
    // If the cardStackId is not a string, it means there's no cardStack which means means no lesson
    return courseDate.attributes.cardStackId !== null
  }

  /**
   * Count the number of lessons in the given date range.
   */
  countCourseDateLessons(course: Course, startDate: string, endDate: string): number {
    let count = 0
    const range = moment.range(moment(startDate), moment(endDate))
    const dateStrings = Array.from(range.by("days")).map((moment) => moment.format("YYYY-MM-DD"))
    for (const dateString of dateStrings) {
      if (this.doesCourseDateHaveLesson(course, dateString)) count++
    }
    return count
  }

  /**
   * Check if the courseDate has a unit that starts and ends on the same day
   */
  doesCourseDateHaveTinyUnit(course: Course, dateString: string): boolean {
    let courseDate = findCourseDateCustom(course, dateString)
    //if there's not a courseDate there's definitely no unit
    if (courseDate === undefined) return false

    let hasAOneDayUnit = _.size(
      _.intersection(courseDate.attributes.unitStart, courseDate.attributes.unitEnd)
    )
    // if there is a small unit, the user must take action
    return hasAOneDayUnit > 0
  }

  /**
   * This pulls a single lesson backward to desired date from which the action was called.
   * If there is a lesson on the target date the user wants to pull back to, this lesson is deleted - notify the user the lesson will be lost
   * Useful as a button on a menu, such as lesson-menu
   * This can't be executed if there is a unit that starts and ends on the SAME date - the user must manually go and remove it on their own
   * Before calling this method, we have to call `doesCourseDateHaveTinyUnit` to avoid error mentioned above
   */
  pullLessonBackwards(
    dateString: string,
    course: Course,
    planbook: Planbook,
    rotationCalendar: RotationCalendar
  ) {
    if (this.doesCourseDateHaveTinyUnit(course, dateString) === false) {
      this.dispatcher.dispatchPreparedAction(
        pullLessonBackwardsAction({
          dateString: dateString,
          course: course,
          planbook: planbook,
          rotationCalendar: rotationCalendar,
        })
      )
    }
  }
  /**
   * This pulls all lessons backward to desired date from which the action was called
   * It's useful as a button on a menu, such as day-menu
   * Iterates through each lesson and pulls it back, using the code that pulls back an individual lesson
   * If there are lessons on the target date the user wants to pull back to, these lessons are deleted - notify the user the lessons will be lost
   * This cannot be executed if a unit starts or ends on target date - user must delete unit first.
   * We check for a unit on pullLessonBackwards to avoid errors
   */
  pullMultipleLessonsBackwards(
    dateString: string,
    courses: Array<Course>,
    planbook: Planbook,
    rotationCalendar: RotationCalendar
  ) {
    this.dispatcher.dispatchPreparedAction(
      preparePullMulitpleLessonsBackwards({
        dateString: dateString,
        courses: courses,
        planbook: planbook,
        rotationCalendar: rotationCalendar,
      })
    )
  }

  /**
  -------------------------------------------------------------------------------------------------------
  Erasing days
  --------------------------------------------------------------------------------------------------------
  */

  /**
   * This method erases all lessons from the specified day of the planbook by locating the courseDate and related cardstacks,
   * resetting the cardstack attributes to null and resetting the version history.
   * This is only called if the user chooses to delete the lesson history to bump new lessons into this day.
   */
  eraseAllLessonsForDay(courses: Array<Course>, dateString: string, planbookId: string) {
    this.dispatcher.dispatchPreparedAction(
      prepareEraseAllLessonsForDay({
        courses: courses,
        dateString: dateString,
        planbookId: planbookId,
      })
    )
  }

  eraseAllLessonsForDays(
    courses: Array<Course>,
    startDate: string,
    endDate: string,
    planbookId: string,
    rotationCalendar: RotationCalendar
  ) {
    const validSchoolDays = rotationCalendar.attributes.schoolDays
    const range = moment.range(moment(startDate), moment(endDate))
    const dateStrings = Array.from(range.by("days"))
      .filter((moment) => validSchoolDays.includes(moment.weekday()))
      .map((moment) => moment.format("YYYY-MM-DD"))
    if (dateStrings.length === 0) return
    else if (dateStrings.length === 1) {
      this.eraseAllLessonsForDay(courses, dateStrings[0], planbookId)
    } else {
      this.dispatcher.dispatchPreparedAction(
        prepareEraseAllLessonsForDays({
          courses: courses,
          dateStrings,
          planbookId: planbookId,
        })
      )
    }
  }

  // if it's off b/c of a course date annotation, we need to remove the annotation
  // if it's off b/c of the defaults, we need to set isForcedOn
  private _turnCourseOn(course: Course, dateString: string, courseDateIsForcedOn: boolean) {
    let isOffFromCustom =
      includes(course.attributes.calendar.datesOff, dateString) && courseDateIsForcedOn === false
    if (isOffFromCustom) {
      let annotation = pipe(
        course.attributes.calendar.dateAnnotations,
        A.filter(isCourseDateAnnotationSingle),
        A.find((ann) => ann.attributes.dateString === dateString)
      )
      if (annotation) {
        this.dispatcher.dispatchPreparedAction(
          courseDateAnnotationDeleteAction({
            course: course,
            annotation: annotation,
            calledFrom: "day-menu-quick-toggle",
          })
        )
      }
    } else {
      this._forceDateOn(course, dateString)
    }
  }

  private _forceDateOn(course: Course, dateString: string) {
    const existingCourseDate = pipe(
      course.attributes.calendar.dates,
      A.filter(isCourseDateCustom),
      A.find((date) => date.attributes.dateString === dateString)
    )

    this.dispatcher.dispatchPreparedAction(
      courseDateForceOnAction({
        dateString: dateString,
        course: course,
        shouldAddCourseDate: existingCourseDate === undefined,
        courseDateToAdd: existingCourseDate ?? {
          id: dateString,
          type: "course-date-custom",
          attributes: createCourseDateCustomAttributes({
            dateString: dateString,
            isForcedOn: true,
          }),
        },
      })
    )
  }

  /**
   * Turn a class back on for one day, whatever is holding it off. Only the annotations carrying
   * `isOff` go — those are what `datesOff` is derived from, and every one has to go or the day
   * stays off — while the day's notes are left exactly where they are. A date whose whole rotation
   * is off is then forced on, which is the only way to cancel a default.
   */
  turnDateOn({
    course,
    dateString,
    calledFrom,
  }: {
    course: Course
    dateString: string
    calledFrom: string
  }) {
    const offMarkers = _.filter(
      course.attributes.calendar.dateAnnotations,
      (annotation) =>
        isCourseDateAnnotationSingle(annotation) &&
        annotation.attributes.dateString === dateString &&
        annotation.attributes.isOff === true
    ) as Array<CourseDateAnnotationSingle>

    for (const annotation of offMarkers) {
      this.dispatcher.dispatchPreparedAction(
        courseDateAnnotationDeleteAction({ course, annotation, calledFrom })
      )
    }

    // Nothing custom was holding it off, so the rotation's own default is — cancel that for today.
    const courseDate = findCourseDateCustom(course, dateString)
    const dateIsForcedOn = courseDate?.attributes.isForcedOn === true
    if (offMarkers.length === 0 && !dateIsForcedOn) this._forceDateOn(course, dateString)
  }

  // if it's on b/c of a isForcedOn, we need to set that to false
  // if it's on b/c of a default, we need to create a date annotation
  private _turnCourseOff(course, dateString, courseDateIsForcedOn) {
    if (courseDateIsForcedOn) {
      this.dispatcher.dispatchAction(COURSE_DATE_UNFORCE_ON, {
        dateString: dateString,
        course: course,
      })
    } else {
      let annotation = new DateAnnotation({
        id: ObjectId.create(),
        type: "date-annotation-course-custom-single",
        attributes: {
          dateString: dateString,
          showAnnotation: false,
          isOff: true,
        },
      })
      this.dispatcher.dispatchAction(COURSE_DATE_ANNOTATION_CREATE, {
        course: course,
        annotation: annotation,
        originalDatesOff: course.attributes.calendar.datesOff,
        source: "day-menu-quick-toggle",
      })
    }
  }

  private _turnOffDefault(course: Course, rotation: RotationCalendarRotation, semesterId: string) {
    let rotationId = rotation.id
    let rotationTitle = rotation.title
    let annotation = new DateAnnotation({
      id: `${semesterId}:${rotationId}`,
      type: "date-annotation-course-default",
      attributes: {
        rotationId: rotationId,
        semesterId: semesterId,
        isOff: true,
      },
    })
    // dispatch annotation WITHOUT dates to force on
    this.dispatcher.dispatchAction(COURSE_DATE_ANNOTATION_CREATE, {
      course: course,
      annotation: annotation,
      originalDatesOff: course.attributes.calendar.datesOff,
      calledFrom: "schedule-screen",
      rotationTitle: rotationTitle,
      datesForcedOn: null,
    })
  }

  private _turnOnDefault(
    course: Course,
    rotationCalendar: RotationCalendar,
    rotation: RotationCalendarRotation,
    semesterId: string
  ) {
    let rotationId = rotation.id
    let courseDatesOff = course.attributes.calendar.datesOff
    let foundDates = chain(rotationCalendar.attributes.dates)
      .filter(
        (date: RotationCalendarDate) =>
          date.attributes.semesterId === semesterId && date.attributes.rotationId === rotationId
      )
      .map((date: RotationCalendarDate) => date.attributes.dateString)
      .filter((dateString: string) => includes(courseDatesOff, dateString))
      .value()

    let formattedFoundDates = map(foundDates, (dateString) =>
      dateFns.format(dateString, "ddd, MMM D")
    )

    let hasDateAnnotation = find(course.attributes.calendar.dateAnnotations, (annotation) => {
      return (
        annotation.type === "date-annotation-course-default" &&
        annotation.attributes.rotationId === rotation.id &&
        annotation.attributes.semesterId === semesterId
      )
    })
    if (hasDateAnnotation === undefined) {
      let rotationId = rotation.id
      let rotationTitle = rotation.title

      let annotation = new DateAnnotation({
        id: `${semesterId}:${rotationId}`,
        type: "date-annotation-course-default",
        attributes: {
          rotationId: rotationId,
          semesterId: semesterId,
          isOff: true,
        },
      })
      this.dispatcher.dispatchAction(COURSE_DATE_ANNOTATION_CREATE, {
        course: course,
        annotation: annotation,
        originalDatesOff: course.attributes.calendar.datesOff,
        rotationTitle: rotationTitle,
        calledFrom: "schedule-screen",
      })
    } else {
      this.dispatcher.dispatchPreparedAction(
        courseDateAnnotationDeleteAction({
          course: course,
          annotation: hasDateAnnotation,
          calledFrom: "schedule-screen",
          rotationTitle: rotation.title,
        })
      )
    }
    if (foundDates.length > 0) {
      return this.dialog
        .blank("dialogs/ask-to-turn-dates-on", {
          lessonCount: foundDates.length,
          foundDates: formattedFoundDates,
          rotationTitle: rotation.title,
          className: "dialog--create-annotation",
        })
        .then((turnDatesOn: boolean) => {
          if (turnDatesOn) {
            this.dispatcher.dispatchAction(COURSE_DATE_ANNOTATION_DELETE_MULTIPLE, {
              course: course,
              dateStrings: foundDates,
            })
          }
        })
        .catch((error) => {
          if (error instanceof Error) handleError(error)
        })
    } else {
      return undefined
    }
  }
}
